import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSetRecoilState } from 'recoil';
import { FormControl, FormLabel, Select, VStack } from '@tq/ui-components';

import { SearchText, UploadConsultees, IsRegisteredDropDown } from 'components';
import {
  manageConsulteeOrganisationFilterState,
  manageConsulteeRegistrationFilterState,
  manageConsulteeSearchState,
} from 'store';
import { filterSelectStyle } from 'styles';
import { filterContainerStyle } from '../../../styles';
import { useAllParentOrganisations } from '../../../../../api';
import { Consultee, ValueLabel } from 'types';

type ManageUsersFiltersProps = {
  consultees: Consultee[];
  totalCount: number;
};

export const UsersFilters: React.FC<ManageUsersFiltersProps> = ({
  consultees,
  totalCount,
}) => {
  const { t } = useTranslation('manage_consultees');
  const { data: parentOrganisations } = useAllParentOrganisations();
  const organisations = parentOrganisations?.parentOrganisations ?? [];
  const setOrganisationFilter = useSetRecoilState(manageConsulteeOrganisationFilterState);

  const isRegisteredOptions: ValueLabel<string>[] = [
    { value: 'NotDeleted', label: t('all', 'All') },
    { value: 'Registered', label: t('yes', 'Yes') },
    { value: 'NotRegistered', label: t('no', 'No') },
  ];

  return (
    <VStack
      id='user-filter'
      sx={{ ...filterContainerStyle, width: '100%', mb: 0 }}
      gridGap={2}
      alignItems='flex-end'
    >
      <FormControl
        flex='1 1 220%'
        mt={{ base: 0, lg: '0.5rem' }}
        mr={{ base: 0, lg: '1.25rem' }}
      >
        <SearchText
          searchState={manageConsulteeSearchState}
          placeholderText={t('search_placeholder', 'Search by name or email')}
          recordCount={consultees.length}
          totalCount={totalCount}
        />
      </FormControl>
      <FormControl
        flex='1 1 180%'
        mx={{ base: 0, lg: '1.25rem' }}
        my={{ base: '1rem', lg: 0 }}
      >
        <FormLabel htmlFor='organisation' mb={{ base: 0, lg: '.75rem' }}>
          {t('organisation_label', 'Organisation')}:
        </FormLabel>
        <Select
          inputId='organisation'
          onChange={(value) => setOrganisationFilter(value === 'All' ? '' : value)}
          defaultValue='All'
          sx={filterSelectStyle}
          aria-label='Organisation selection dropdown'
        >
          <option key='all' value='All'>
            {t('all', 'All')}
          </option>
          {organisations.map((org) => (
            <option key={org.value} value={org.label}>
              {org.label}
            </option>
          ))}
        </Select>
      </FormControl>
      <FormControl flex='1 1 80%' mx={{ base: 0, lg: '1.25rem' }} my={{ base: '1rem', lg: 0 }}>
        <IsRegisteredDropDown
          inputId='is-registered'
          recoilState={manageConsulteeRegistrationFilterState}
          options={isRegisteredOptions}
          label={`${t('is_registered_label', 'Is registered')}:`}
        />
      </FormControl>
      <UploadConsultees />
    </VStack>
  );
};
